import React, { useEffect, useState } from 'react';
import { db } from '../firebase';
import { collection, getDocs, doc, getDoc } from 'firebase/firestore';

/**
 * SkillSheetsSelector
 * - Lets user select skill sheets for the lab day from all available skill sheets.
 * - Used in NewLabDay to the right of Lab Information.
 * - Now supports multi-add (count per sheet), no descriptions, and "+" icon UI.
 */
const SkillSheetsSelector = ({ selectedSkillSheets, setSelectedSkillSheets }) => {
  const [allSkillSheets, setAllSkillSheets] = useState([]);
  const [categories, setCategories] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [search, setSearch] = useState('');
  const [filterCat, setFilterCat] = useState('');
  const [loading, setLoading] = useState(true);

  // selectedSkillSheets is now an array of objects: [{id, count}]
  // For backward compatibility, upgrade if needed
  useEffect(() => {
    if (selectedSkillSheets.length > 0 && typeof selectedSkillSheets[0] === 'string') {
      setSelectedSkillSheets(selectedSkillSheets.map(id => ({ id, count: 1 })));
    }
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    const fetchSkillSheets = async () => {
      setLoading(true);
      const snap = await getDocs(collection(db, 'skillSheetTemplates'));
      setAllSkillSheets(snap.docs.map(doc => ({ id: doc.id, ...doc.data() })));
      setLoading(false);
    };
    const fetchCategories = async () => {
      const ref = doc(db, 'skillSheetCategories', 'main');
      const snap = await getDoc(ref);
      if (snap.exists()) setCategories(snap.data().categories || []);
    };
    fetchSkillSheets();
    fetchCategories();
  }, []);

  const filteredSheets = allSkillSheets.filter(sheet => {
    const matchesSearch = sheet.name.toLowerCase().includes(search.toLowerCase());
    const matchesCat = !filterCat || (categories.find(cat => cat.id === filterCat)?.sheets || []).includes(sheet.id);
    return matchesSearch && (filterCat ? matchesCat : true);
  });

  // Add or increment count for a skill sheet
  const addSkillSheet = (id) => {
    setSelectedSkillSheets(prev => {
      const found = prev.find(s => s.id === id);
      if (found) {
        return prev.map(s => s.id === id ? { ...s, count: s.count + 1 } : s);
      } else {
        return [...prev, { id, count: 1 }];
      }
    });
  };

  // Remove or decrement count for a skill sheet
  const removeSkillSheet = (id) => {
    setSelectedSkillSheets(prev => {
      const found = prev.find(s => s.id === id);
      if (!found) return prev;
      if (found.count > 1) {
        return prev.map(s => s.id === id ? { ...s, count: s.count - 1 } : s);
      } else {
        return prev.filter(s => s.id !== id);
      }
    });
  };

  return (
    <div className="p-4 border-2 border-primary rounded-2xl bg-white/80 shadow-lg min-h-[340px] flex flex-col">
      <h2 className="text-xl font-bold mb-4 text-primary">Skill Sheets</h2>
      <button
        className="bg-primary text-white px-4 py-2 rounded shadow hover:bg-secondary hover:text-primary font-semibold mb-2"
        onClick={() => setShowModal(true)}
      >
        + Skill Sheet
      </button>
      {selectedSkillSheets.length > 0 && (
        <div className="flex flex-wrap gap-2 mt-2">
          {selectedSkillSheets.map(({ id, count }) => {
            const sheet = allSkillSheets.find(s => s.id === id);
            return sheet ? (
              <div key={id} className="flex items-center gap-1 text-sm bg-gray-100 rounded-full px-3 py-1 shadow border border-primary">
                <span className="font-semibold text-text">{sheet.name}</span>
                {count > 1 && <span className="ml-1 bg-primary text-white rounded-full px-2 py-0.5 text-xs font-bold">{count}</span>}
                <button className="ml-2 text-red-500 hover:text-red-700 text-lg font-bold" onClick={() => removeSkillSheet(id)} title="Remove one">×</button>
              </div>
            ) : null;
          })}
        </div>
      )}
      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" onClick={e => { if (e.target === e.currentTarget) setShowModal(false); }}>
          <div className="bg-white rounded-xl p-6 w-full max-w-md relative shadow-xl border border-primary">
            <button onClick={() => setShowModal(false)} className="absolute top-2 right-2 text-xl font-bold">×</button>
            <h3 className="text-lg font-bold mb-2 text-primary">Select Skill Sheets</h3>
            <input
              className="w-full border rounded px-2 py-1 mb-2"
              placeholder="Search skill sheets..."
              value={search}
              onChange={e => setSearch(e.target.value)}
            />
            <select className="w-full border rounded px-2 py-1 mb-2" value={filterCat} onChange={e => setFilterCat(e.target.value)}>
              <option value="">All Categories</option>
              {categories.map(cat => (
                <option key={cat.id} value={cat.id}>{cat.name}</option>
              ))}
            </select>
            <div className="overflow-y-auto max-h-64 flex flex-col gap-2">
              {loading ? (
                <div className="text-gray-500">Loading skill sheets...</div>
              ) : filteredSheets.length === 0 ? (
                <div className="text-gray-400 italic">No skill sheets found.</div>
              ) : (
                filteredSheets.map(sheet => {
                  const selected = selectedSkillSheets.find(s => s.id === sheet.id);
                  return (
                    <div key={sheet.id} className="flex items-center gap-2 cursor-pointer bg-gray-50 hover:bg-blue-50 rounded px-2 py-1">
                      <button
                        className="bg-primary text-white rounded-full w-7 h-7 flex items-center justify-center text-lg font-bold hover:bg-secondary hover:text-primary focus:outline-none"
                        onClick={() => addSkillSheet(sheet.id)}
                        title={selected ? `Add another` : `Add`}
                        type="button"
                      >
                        +
                      </button>
                      <span className="font-semibold text-text">{sheet.name}</span>
                      {selected && selected.count > 1 && (
                        <span className="ml-1 bg-primary text-white rounded-full px-2 py-0.5 text-xs font-bold">{selected.count}</span>
                      )}
                    </div>
                  );
                })
              )}
            </div>
            <button className="mt-4 bg-primary text-white px-4 py-2 rounded shadow hover:bg-secondary hover:text-primary font-semibold w-full" onClick={() => setShowModal(false)}>Done</button>
          </div>
        </div>
      )}
    </div>
  );
};

export default SkillSheetsSelector;
